import {
  LogInIcon,
  PlusIcon,
  // Admin sidebar icons are imported from lucide directly and are the *same components*
  // the pages pass to `ManagementPage`. The custom icon-utils set has no equivalents for
  // several of these, and approximating them is what left the sidebar and the page
  // headers showing different pictures of the same thing.
  Award,
  BookMarked,
  CalendarDays,
  Coins,
  CreditCard,
  DollarSign,
  Gavel,
  Receipt,
  Star
} from "lucide-react";
import {
  DashboardIcon,
  UsersIcon,
  SettingsIcon,
  HelpIcon,
  SearchIcon,
  ShieldIcon,
  BuildingIcon,
  BellIcon,
  DollarSignIcon,
  ReceiptIcon,
  FileDescriptionIcon,
  CalendarIcon,
  UserIcon,
  StarIcon,
  UserTieIcon,
  AwardIcon,
  StethoscopeIcon,
  ClipboardIcon,
  UserEditIcon,
  CreditCardIcon
} from "@/utilities/icon-utils";
import type { UserType } from "@/domain/core";
import type { NavigationConfig, NavigationItem } from "@/types/core";

// Admin User Navigation
const adminNavigation: NavigationConfig = {
  main: [
    {
      title: "Dashboard",
      url: "/admin",
      icon: DashboardIcon
    },
    {
      title: "Applications",
      url: "/admin/applications",
      icon: ClipboardIcon
    },
    {
      title: "Practices",
      url: "/admin/practices",
      icon: BuildingIcon
    },
    {
      title: "Professionals",
      url: "/admin/professionals",
      icon: UserTieIcon
    },
    {
      title: "Users",
      url: "/admin/users",
      icon: UsersIcon
    },
    {
      title: "Shifts",
      url: "/admin/shifts",
      icon: CalendarDays
    },
    {
      title: "Catalogue",
      url: "/admin/catalogue",
      icon: BookMarked
    },
    {
      title: "Loyalty",
      url: "/admin/tiers",
      icon: Award
    },
    {
      title: "Operations",
      // No page of its own — the section exists to hold the consoles below.
      url: "/admin/operations",
      icon: ClipboardIcon,
      children: [
        {
          title: "Payments",
          url: "/admin/payments",
          icon: DollarSign
        },
        {
          title: "Disputes",
          url: "/admin/disputes",
          icon: Gavel
        },
        {
          title: "Ratings",
          url: "/admin/ratings",
          icon: Star
        }
      ]
    },
    {
      title: "Billing",
      // Tempii's own receivables, kept apart from Operations: these are money Tempii
      // collects, where Payments records money Tempii never touches.
      url: "/admin/billing",
      icon: CreditCardIcon,
      children: [
        {
          title: "Shift Fees",
          url: "/admin/fees",
          icon: Receipt
        },
        {
          title: "Subscription Invoices",
          url: "/admin/subscription-invoices",
          icon: CreditCard
        },
        {
          title: "Pricing",
          url: "/admin/pricing",
          icon: Coins
        }
      ]
    }
  ],
  secondary: [
    {
      title: "Notifications",
      url: "/admin/notifications",
      icon: BellIcon
    },
    {
      title: "Help",
      url: "/admin/help",
      icon: HelpIcon
    },
    {
      title: "Search",
      url: "/admin/search",
      icon: SearchIcon
    }
  ]
};

// Practice User Navigation
const practiceNavigation: NavigationConfig = {
  main: [
    {
      title: "Dashboard",
      url: "/practice",
      icon: DashboardIcon
    },
    {
      title: "Shifts",
      url: "/practice/shifts",
      icon: CalendarIcon,
      // Calendar, Scheduler, Conflicts and a separate List were listed here with no
      // route behind them. They never rendered while NavMain ignored children; now
      // that it does not, they would be links to the catch-all.
      children: [
        {
          title: "All Shifts",
          url: "/practice/shifts",
          icon: CalendarIcon
        },
        {
          title: "Post Shift",
          url: "/practice/shifts/post",
          icon: PlusIcon
        },
        {
          title: "Shift Templates",
          url: "/practice/shifts/templates",
          icon: FileDescriptionIcon
        }
      ]
    },
    {
      title: "Payments",
      url: "/practice/payments",
      icon: ReceiptIcon
    },
    {
      title: "Loyality",
      url: "/practice/loyality",
      icon: AwardIcon
    },
    {
      title: "Subscription",
      url: "/practice/subscription",
      icon: CreditCardIcon
    },
    {
      title: "Profile",
      url: "/practice/profile",
      icon: UserIcon,
      children: [
        {
          title: "Overview",
          url: "/practice/profile",
          icon: UserIcon
        },
        {
          title: "Profile Editor",
          url: "/practice/profile/editor",
          icon: UserEditIcon
        }
      ]
    },
    {
      title: "Verification",
      url: "/practice/verification",
      icon: ShieldIcon
    }
  ],
  secondary: [
    {
      title: "Notifications",
      url: "/practice/notifications",
      icon: BellIcon
    },
    {
      title: "Settings",
      url: "/practice/settings",
      icon: SettingsIcon
    },
    {
      title: "Help",
      url: "/practice/help",
      icon: HelpIcon
    },
    {
      title: "Search",
      url: "/practice/search",
      icon: SearchIcon
    }
  ]
};

// Professional User Navigation
const professionalNavigation: NavigationConfig = {
  main: [
    {
      title: "Dashboard",
      url: "/professional",
      icon: DashboardIcon
    },
    {
      // One item, because there is one page. This was a group of six -- All Shifts,
      // Personal Calendar, Shift List, Shift Applications, Shift Notes, Follow-up
      // Scheduler -- and every one of them routed to the same component. The shifts page
      // already holds the tabbed list (applications is its default tab), the recommended
      // feed and the personal calendar; notes and follow-up had nothing behind them but
      // an empty-state card.
      title: "Shifts",
      url: "/professional/shifts",
      icon: CalendarIcon
    },
    {
      title: "Earnings",
      url: "/professional/payments",
      icon: ReceiptIcon
    },
    {
      title: "Loyality",
      url: "/professional/loyality",
      icon: AwardIcon
    },
    {
      title: "Referrals",
      url: "/professional/referrals",
      icon: UsersIcon
    },
    {
      title: "Profile",
      url: "/professional/profile",
      icon: UserIcon,
      children: [
        {
          title: "Overview",
          url: "/professional/profile",
          icon: UserIcon
        },
        {
          title: "Profile Editor",
          url: "/professional/profile/editor",
          icon: UserEditIcon
        },
        {
          title: "Certificate Management",
          url: "/professional/profile/certificates",
          icon: AwardIcon
        },
        {
          title: "Skill Management",
          url: "/professional/profile/skills",
          icon: StarIcon
        },
        {
          title: "Specialty Management",
          url: "/professional/profile/specialties",
          icon: StethoscopeIcon
        },
        {
          title: "Rate Management",
          url: "/professional/profile/rates",
          icon: DollarSignIcon
        }
      ]
    },
    {
      title: "Verification",
      url: "/professional/verification",
      icon: ShieldIcon
    }
  ],
  secondary: [
    {
      title: "Notifications",
      url: "/professional/notifications",
      icon: BellIcon
    },
    {
      title: "Settings",
      url: "/professional/settings",
      icon: SettingsIcon
    },
    {
      title: "Help",
      url: "/professional/help",
      icon: HelpIcon
    },
    {
      title: "Search",
      url: "/professional/search",
      icon: SearchIcon
    }
  ]
};

// Navigation configuration mapping
export const NAVIGATION_CONFIG: Record<UserType, NavigationConfig> = {
  admin: adminNavigation,
  // Support shares the admin console; sensitive actions inside it are gated
  // per-action rather than by hiding the whole section.
  support: adminNavigation,
  practice: practiceNavigation,
  professional: professionalNavigation,
  none: {
    main: [],
    secondary: []
  }
};

/**
 * Drops hidden items (and hidden children) so sidebar/menus omit them
 * while the underlying routes and config entries remain.
 */
function visibleNavigationItems(items: NavigationItem[]): NavigationItem[] {
  return items
    .filter((item) => !item.hidden)
    .map((item) =>
      item.children
        ? { ...item, children: visibleNavigationItems(item.children) }
        : item
    );
}

function visibleNavigation(config: NavigationConfig): NavigationConfig {
  return {
    main: visibleNavigationItems(config.main),
    secondary: visibleNavigationItems(config.secondary)
  };
}

/**
 * Gets the appropriate navigation configuration based on user type
 * @param userType - User type from API response
 * @returns Navigation configuration for the user
 */
export function getNavigationConfig(userType: UserType): NavigationConfig {
  // Check if we have a valid user type
  if (!userType || userType === "none") {
    // Return default navigation if no specific user type found
    return {
      main: [
        {
          title: "Login",
          url: "/login",
          icon: LogInIcon
        }
      ],
      secondary: []
    };
  }

  // Return the navigation configuration for the user type
  return visibleNavigation(NAVIGATION_CONFIG[userType]);
}
